import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", "input", "submitBtn",
    "loading", "error", "errorMessage",
    "answer", "answerText",
    "sources", "sourcesList",
    "empty"
  ]

  search(event) {
    event.preventDefault()
    const query = this.inputTarget.value.trim()
    if (query.length < 3) return

    this.showLoading()
    this.fetchAnswer(query)
  }

  quickSearch(event) {
    event.preventDefault()
    const query = event.currentTarget.dataset.query
    this.inputTarget.value = query
    this.showLoading()
    this.fetchAnswer(query)
  }

  async fetchAnswer(query) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch("/ai-search/query.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ query })
      })

      const data = await response.json()

      if (!response.ok || data.error) {
        this.showError(data.error || "Something went wrong. Please try again.")
        return
      }

      this.showAnswer(data)
    } catch (error) {
      console.error("AI Search error:", error)
      this.showError("Unable to reach the search service. Please try again.")
    }
  }

  showLoading() {
    this.emptyTarget.classList.add("d-none")
    this.errorTarget.classList.add("d-none")
    this.answerTarget.classList.add("d-none")
    this.loadingTarget.classList.remove("d-none")
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span> Searching...'
  }

  showError(message) {
    this.loadingTarget.classList.add("d-none")
    this.emptyTarget.classList.add("d-none")
    this.answerTarget.classList.add("d-none")
    this.errorTarget.classList.remove("d-none")
    this.errorMessageTarget.textContent = message
    this.resetButton()
  }

  showAnswer(data) {
    this.loadingTarget.classList.add("d-none")
    this.emptyTarget.classList.add("d-none")
    this.errorTarget.classList.add("d-none")
    this.answerTarget.classList.remove("d-none")

    // Render the AI response (convert markdown-like formatting to HTML)
    this.answerTextTarget.innerHTML = this.formatResponse(data.response || "No answer available.")

    // Render sources
    if (data.sources && data.sources.length > 0) {
      this.sourcesTarget.classList.remove("d-none")
      this.sourcesListTarget.innerHTML = data.sources.map(source => this.sourceCard(source)).join("")
    } else {
      this.sourcesTarget.classList.add("d-none")
    }

    this.resetButton()
  }

  resetButton() {
    this.submitBtnTarget.disabled = false
    this.submitBtnTarget.innerHTML = '<i class="bi bi-send me-1"></i> Ask'
  }

  formatResponse(text) {
    // Convert markdown bold **text** to <strong>
    let html = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")

    // Convert markdown links [text](url) to <a>
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

    // Convert numbered lists
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, "<li>$2</li>")

    // Convert bullet lists
    html = html.replace(/^[-*]\s+(.+)$/gm, "<li>$1</li>")

    // Wrap consecutive <li> in <ul>
    html = html.replace(/((?:<li>.*?<\/li>\s*)+)/g, "<ul>$1</ul>")

    // Convert line breaks to paragraphs
    const paragraphs = html.split(/\n\n+/).filter(p => p.trim())
    if (paragraphs.length > 1) {
      html = paragraphs.map(p => {
        p = p.trim()
        if (p.startsWith("<ul>") || p.startsWith("<ol>")) return p
        return `<p>${p}</p>`
      }).join("")
    }

    // Clean up stray newlines
    html = html.replace(/\n/g, "<br>")

    return html
  }

  sourceCard(source) {
    const filename = source.filename || "Source"
    const displayName = filename.split("/").pop().replace(/\.\w+$/, "").replace(/[-_]/g, " ")
    const score = source.score ? `${Math.round(source.score * 100)}% match` : ""
    const snippet = source.text || ""

    return `
      <div class="col-12 col-md-6">
        <div class="ai-search-source-card">
          <div class="fw-medium text-capitalize mb-1">${this.escapeHtml(displayName)}</div>
          ${snippet ? `<div class="text-muted small">${this.escapeHtml(snippet)}</div>` : ""}
          ${score ? `<div class="mt-1"><span class="badge bg-light text-muted">${score}</span></div>` : ""}
        </div>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
